/**
 * Allocation category configuration
 * Maps categories to colors and Lucide icons
 */
import { getLuminance, mix, toHex } from 'color2k';

// Color palette - using CSS variables
export const COLORS = {
  Blue: {
    400: 'rgb(var(--blue-p-400))',
    dark: 'rgb(var(--blue-p-dark))',
  },
  Teal: {
    400: 'rgb(var(--teal-p-400))',
    dark: 'rgb(var(--teal-p-dark))',
  },
  YellowP: {
    400: 'rgb(var(--yellow-p-400))',
    dark: 'rgb(var(--yellow-p-dark))',
  },
  GreenP: {
    400: 'rgb(var(--green-p-400))',
    dark: 'rgb(var(--green-p-dark))',
  },
  GreenFluo: {
    400: 'rgba(var(--green-fluo-400))',
    dark: 'rgb(var(--green-fluo-dark))',
  },
  Indigo: {
    400: 'rgba(var(--indigo-p-400))',
    dark: 'rgb(var(--indigo-p-dark))',
  },
};

export const ALLOCATION_CATEGORIES = {
  keynote: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'presentation',
    label: 'Keynote',
  },
  reception: {
    color: COLORS.Indigo[400],
    darkColor: COLORS.Indigo.dark,
    icon: 'glass-water',
    label: 'Reception',
  },
  breakfast: {
    color: COLORS.Teal[400],
    darkColor: COLORS.Teal.dark,
    icon: 'utensils',
    label: 'Breakfast',
  },
  dinner: {
    color: COLORS.Teal[400],
    darkColor: COLORS.Teal.dark,
    icon: 'cooking-pot',
    label: 'Dinner',
  },
  panel: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'users',
    label: 'Panel',
  },
  workshop: {
    color: COLORS.GreenP[400],
    darkColor: COLORS.GreenP.dark,
    icon: 'clipboard',
    label: 'Workshop',
  },
  'set-up': {
    color: COLORS.Blue[400],
    darkColor: COLORS.Blue.dark,
    icon: 'wrench',
    label: 'Set-up',
  },
  'tear-down': {
    color: COLORS.GreenFluo[400],
    darkColor: COLORS.GreenFluo.dark,
    icon: 'archive',
    label: 'Tear-down',
  },
  croissant: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'croissant',
    label: 'Croissant',
  },
  drill: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'drill',
    label: 'Drill',
  },
  hamburger: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'hamburger',
    label: 'Hamburger',
  },
  cookie: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'cookie',
    label: 'Cookie',
  },
  salad: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'salad',
    label: 'Salad',
  },
  beef: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'beef',
    label: 'Beef',
  },
  pizza: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'pizza',
    label: 'Pizza',
  },
  carrot: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'carrot',
    label: 'Carrot ',
  },
  icecream: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'ice-cream-cone',
    label: 'Ice Cream',
  },
  fish: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'fish',
    label: 'Fish',
  },
  shrimp: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'shrimp',
    label: 'Shrimp',
  },
  martini: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'martini',
    label: 'Cocktail',
  },
  coffee: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'coffee',
    label: 'Coffee',
  },
  alarm_clock_off: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'alarm-clock-off',
    label: 'Time Off',
  },
  cake: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'cake',
    label: 'Cake',
  },
  beer: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'beer',
    label: 'Beer',
  },
  vegan: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'vegan',
    label: 'Vegan',
  },
  ticket: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'ticket',
    label: 'Ticket',
  },
  balloon: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'balloon',
    label: 'Balloon',
  },
  truck: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'truck',
    label: 'Truck',
  },
  package: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'package',
    label: 'Package',
  },
  graduation_cap: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'graduation-cap',
    label: 'Graduation Cap',
  },
  trophy: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'trophy',
    label: 'Trophy',
  },
  gift: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'gift',
    label: 'Gift',
  },
  party_popper: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'party-popper',
    label: 'Party Popper',
  },
  trophy: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'trophy',
    label: 'Trophy',
  },
  hand_platter: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'hand-platter',
    label: 'Server',
  },
  trees: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'trees',
    label: 'Trees',
  },
  projector: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'projector',
    label: 'Projector',
  },
  music: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'music',
    label: 'Music',
  },
  turntable: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'turntable',
    label: 'Turntable',
  },
  speaker: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'speaker',
    label: 'Speaker',
  },
  brush_cleaning: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'brush-cleaning',
    label: 'Cleaning Brush',
  },
  check_check: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'check-check',
    label: 'Check',
  },
  mic_vocal: {
    color: COLORS.YellowP[400],
    darkColor: COLORS.YellowP.dark,
    icon: 'mic-vocal',
    label: 'Microphone',
  },
};

// Default fallback category
export const DEFAULT_CATEGORY = {
  color: COLORS.GreenP[400],
  darkColor: COLORS.GreenP.dark,
  icon: 'calendar',
  label: 'Event',
};

/**
 * Get category configuration
 * @param {string} category - Category name
 * @returns {object} Category configuration with color, darkColor, icon, and label
 */
export function getCategoryConfig(category) {
  if (!category) return DEFAULT_CATEGORY;
  return ALLOCATION_CATEGORIES[category] || DEFAULT_CATEGORY;
}

/**
 * Icon for a selected allocation category only.
 * Unknown or empty categories render no icon.
 */
export function getCategoryIcon(category) {
  if (!category) return null;
  return ALLOCATION_CATEGORIES[category]?.icon || null;
}

/**
 * Get all available categories
 * @returns {array} Array of category objects with id and config
 */
export function getAllCategories() {
  return Object.entries(ALLOCATION_CATEGORIES).map(([id, config]) => ({
    id,
    ...config,
  }));
}

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

export const DISCO_COLOR = 'disco';

export const CUSTOM_COLOR_SWATCHES = [
  '#ef4444',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#14b8a6',
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
];

/**
 * Event left-border stripe color (metadata.border_color).
 * Falls back to legacy metadata.color from early implementations.
 * @param {object|null|undefined} metadata
 * @returns {string|null}
 */
export function parseEventBorderColor(metadata) {
  const color = metadata?.border_color ?? metadata?.color;
  return color && HEX_RE.test(color) ? color : null;
}

/** @deprecated Use parseEventBorderColor */
export const parseEventColor = parseEventBorderColor;

/**
 * Custom fill color on a single allocation (metadata.color).
 * @param {object|null|undefined} allocation
 * @returns {string|null}
 */
export function parseAllocationColor(allocation) {
  const color = allocation?.metadata?.color;
  return color && HEX_RE.test(color) ? color : null;
}

/**
 * Shared fill color when all allocations use the same metadata.color.
 * @param {Array<object>|null|undefined} allocations
 * @returns {string|null}
 */
export function resolveEventAllocationColor(allocations) {
  if (!allocations?.length) return null;

  const colors = allocations.map(parseAllocationColor).filter(Boolean);
  if (!colors.length) return null;

  const first = colors[0];
  return colors.every(c => c === first) ? first : first;
}

/**
 * All allocations that should receive event-level fill color updates,
 * including parent header rows and nested sub-allocations.
 * @param {object|null|undefined} eventData
 * @returns {Array<object>}
 */
export function collectAllocationsForEventColor(eventData) {
  const raw = eventData?.allocations || [];
  const byId = new Map();

  const add = alloc => {
    if (alloc?.id) byId.set(alloc.id, alloc);
  };

  raw.forEach(alloc => {
    add(alloc);
    (alloc.sub_allocations || []).forEach(add);
  });

  return [...byId.values()];
}

/**
 * @param {string|null} color
 * @returns {Record<string, string>}
 */
export function eventColorBorderStyle(color) {
  if (!color) return {};
  return {
    borderLeftWidth: '5px',
    borderLeftColor: color,
  };
}

/**
 * Whether the allocation (or its parent) is set to the special "disco" mode.
 * @param {object} allocation
 * @param {object|null} [parentAllocation]
 * @returns {boolean}
 */
export function isDiscoAllocation(allocation, parentAllocation = null) {
  if (allocation?.metadata?.color === DISCO_COLOR) return true;
  if (parentAllocation?.metadata?.color === DISCO_COLOR) return true;
  return false;
}

const TEXT_ON_LIGHT = '#111827';
const TEXT_ON_DARK = '#ffffff';

/**
 * Pick a readable text color (white or near-black) for any background.
 * Uses relative luminance so the choice matches WCAG contrast intent.
 */
export function pickReadableTextColor(bg) {
  try {
    return getLuminance(bg) > 0.5 ? TEXT_ON_LIGHT : TEXT_ON_DARK;
  } catch {
    return TEXT_ON_LIGHT;
  }
}

/**
 * Derive a guaranteed-dark hue-matched variant of a hex color.
 * Used as the overlay/accent surface where white text is rendered on top —
 * so it must stay dark regardless of how bright the source color is.
 */
export function deriveDarkColor(hex) {
  if (!hex || !HEX_RE.test(hex)) return TEXT_ON_LIGHT;
  return toHex(mix(hex, '#000000', 0.75));
}

/**
 * Resolve the display colors for an allocation.
 * Precedence: own custom color → parent's custom color → category color.
 * @param {object} allocation - Allocation record
 * @param {object|null} [parentAllocation] - Parent allocation (for sub-allocations)
 * @returns {{ color: string, darkColor: string, textColor: string }}
 *   - color: the main surface background
 *   - darkColor: the accent/overlay surface (always dark, safe for white text)
 *   - textColor: the text color to use on the `color` surface
 */
export function getAllocationColors(allocation, parentAllocation = null) {
  const custom = allocation?.metadata?.color;
  if (custom && HEX_RE.test(custom)) {
    return {
      color: custom,
      darkColor: deriveDarkColor(custom),
      textColor: pickReadableTextColor(custom),
    };
  }
  const parentCustom = parentAllocation?.metadata?.color;
  if (parentCustom && HEX_RE.test(parentCustom)) {
    return {
      color: parentCustom,
      darkColor: deriveDarkColor(parentCustom),
      textColor: pickReadableTextColor(parentCustom),
    };
  }
  const config = getCategoryConfig(allocation?.category);
  // For category colors keep the hand-tuned darkColor as text color,
  // preserving the existing aesthetic where text hue matches the bg.
  return {
    color: config.color,
    darkColor: config.darkColor,
    textColor: config.darkColor,
  };
}
